// MoneyMakerUnlock — the "you did it" beat. The first goal is to get a
// money-maker (a passive-income asset). The moment the kid actually acquires one
// — freedom % crosses from 0 to positive — the whole screen flashes
// "🎉 New money-maker unlocked!" and immediately shows the next goal, so there's
// never a "what now?" gap. Self-mounting watcher; fires once per crossing.

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLifeComputed } from './store.js';
import { useModalSlot } from '../ui/modalQueue.js';
import { Confetti } from '../ui/Confetti.js';
import { useVoice } from '../voice/VoiceContext.js';

export function MoneyMakerUnlock() {
  const pct = useLifeComputed().freedomPct;
  const prev = useRef(pct);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Crossing from "no passive income" to "some" = first money-maker acquired.
    if (prev.current < 1 && pct >= 1) setShow(true);
    prev.current = pct;
  }, [pct]);

  // Priority 88 — above the Payday card (80), so it lands right after the kid
  // invests and Payday closes; below the year-flow (95).
  const { canShow } = useModalSlot('money-maker-unlock', show, 88);

  // MIKO SPEAKS — celebrate the unlock aloud the moment the flash shows.
  const { speak } = useVoice();
  const spokeRef = useRef(false);
  useEffect(() => {
    if (show && canShow) {
      if (!spokeRef.current) {
        spokeRef.current = true;
        void speak("Money machine unlocked! It pays you every single month. Go grab the next one!");
      }
      const t = setTimeout(() => setShow(false), 2800);
      return () => clearTimeout(t);
    }
    if (!show) spokeRef.current = false; // re-arm for the next crossing
  }, [show, canShow, speak]);

  if (!show || !canShow) return null;

  const next = [25, 50, 75, 100].find((m) => pct < m) ?? 100;

  return (
    <div
      onClick={() => setShow(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 94,
        display: 'grid', placeItems: 'center', padding: 24,
        background: 'rgba(10,14,26,0.72)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        cursor: 'pointer',
      }}
    >
      <Confetti count={30} />
      <motion.div
        initial={{ scale: 0.7, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 16 }}
        style={{ textAlign: 'center', position: 'relative' }}
      >
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: [0.5, 1.2, 1] }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ fontSize: 66 }}
        >
          🎉
        </motion.div>
        <div style={{ fontSize: 25, fontWeight: 900, color: '#f4f1ff', lineHeight: 1.15 }}>
          MONEY MACHINE UNLOCKED!
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#cbc2ec', marginTop: 8, maxWidth: 300 }}>
          IT PAYS YOU EVERY MONTH. 🔥
        </div>
        <div
          style={{
            marginTop: 18, display: 'inline-block', padding: '10px 16px', borderRadius: 14,
            border: '2.5px solid #37b6ff', background: 'rgba(55,182,255,0.16)', color: '#7cd0ff',
            fontWeight: 900, fontSize: 14, boxShadow: '0 0 18px #37b6ff55',
          }}
        >
          🎯 NEXT: HIT {next}% FREEDOM
        </div>
        <div style={{ marginTop: 14, fontSize: 12, color: '#978cc2', fontWeight: 800 }}>TAP TO CONTINUE</div>
      </motion.div>
    </div>
  );
}
